import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash/debounce';
import styles from './ChatHistory.module.css';
import { BsChatLeft } from 'react-icons/bs';
import Sidebar from '../Sidebar/Sidebar';
import ImageModal from './ImageModal';

const MessageBubble = ({ message }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [hoveredSentenceIndex, setHoveredSentenceIndex] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const renderContent = () => {
    // Handle different content formats
    let content = message.content || '';
    
    // Handle assistant message with speak object format
    if (message.role === 'assistant' && typeof content === 'object' && content !== null) {
      if (content.speak && content.speak.content) {
        content = content.speak.content;
      } else if (content.content) {
        content = content.content;
      } else {
        // If we have an object with no recognizable format, stringify it
        try {
          content = JSON.stringify(content);
        } catch (e) {
          content = '[Complex object]';
        }
      }
    }
    
    // Extract Turn ID from user messages if present
    if (message.role === 'user' && typeof content === 'string' && content.startsWith('Turn ID:')) {
      const turnIdEnd = content.indexOf('\n');
      if (turnIdEnd > 0) {
        content = content.substring(turnIdEnd + 1);
      }
    }

    // Ensure content is a string for display
    if (typeof content !== 'string') {
      try {
        content = JSON.stringify(content);
      } catch (e) {
        content = '[Complex object]';
      }
    }
    
    // For both user and assistant messages, use a simple approach that preserves content
    if (content) {
      // For user messages, preserve newlines but don't do sentence splitting
      if (message.role === 'user') {
        // Split by newlines while preserving empty lines
        const lines = content.split(/\r?\n/);
        return (
          <>
            {lines.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </>
        );
      }
      
      // For assistant messages, use a much simpler sentence splitting approach
      try {
        // Even simpler approach: split only on clear sentence boundaries
        // This regex looks for sentence endings followed by whitespace and a capital letter
        const sentenceBoundaries = /(?<=[.!?])\s+(?=[A-Z])/g;
        const sentences = content.split(sentenceBoundaries);
        
        // Always display content, even if no split occurred
        return (
          <>
            {sentences.map((sentence, index) => (
              <span 
                key={index}
                className={hoveredSentenceIndex === index ? styles.highlightedSentence : ''}
                onMouseEnter={() => setHoveredSentenceIndex(index)}
                onMouseLeave={() => setHoveredSentenceIndex(null)}
              >
                {sentence}
                {/* Only add space if this isn't the last sentence and the current sentence ends with punctuation */}
                {index < sentences.length - 1 && /[.!?]\s*$/.test(sentence) ? ' ' : ''}
              </span>
            ))}
          </>
        );
      } catch (error) {
        console.error('Error splitting content into sentences:', error);
        // Fallback: display content as-is without sentence splitting
        return (
          <span 
            className={hoveredSentenceIndex === 0 ? styles.highlightedSentence : ''}
            onMouseEnter={() => setHoveredSentenceIndex(0)}
            onMouseLeave={() => setHoveredSentenceIndex(null)}
          >
            {content}
          </span>
        );
      }
    }
    
    return content;
  };

  // Render attached images if present
  const renderImages = () => {
    if (!message.images || !message.images.length) return null;
    
    return (
      <div className={styles.attachedImages}>
        {message.images.map((imageUrl, index) => (
          <div key={index} className={styles.imageAttachment}>
            <img 
              src={imageUrl} 
              alt={`Attachment ${index + 1}`} 
              onClick={() => setSelectedImage(imageUrl)}
              title="Click to view full size" 
            />
          </div>
        ))}
      </div>
    );
  };

  // Determine if the message is long and might need expansion
  const isLongMessage = message.content && typeof message.content === 'string' && 
    (message.content.length > 500 || message.content.split('\n').length > 10);

  return (
    <div className={styles.messageBubble + ' ' + (message.role === 'user' ? styles.userMessage : styles.assistantMessage)}>
      <div className={styles.messageHeader}>
        <span className={styles.messageRole}>
          {message.role === 'user' ? 'You' : 'Pralok'}
        </span>
      </div>
      <div className={`${styles.messageContent} ${isLongMessage && !isExpanded ? styles.collapsedMessage : ''}`}>
        {renderContent()}
      </div>
      {isLongMessage && (
        <button 
          className={styles.expandButton} 
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
      {renderImages()}
      {selectedImage && (
        <ImageModal 
          imageUrl={selectedImage} 
          onClose={() => setSelectedImage(null)} 
        />
      )}
    </div>
  );
};

const ChatHistory = ({ 
  isOpen, 
  onClose, 
  messages = [], 
  isSpeechEnabled, 
  isLoadingInitialMessages = false,
  isLoadingMore: isLoadingMoreProp = false,
  hasMore: hasMoreProp = false,
  isInline = false 
}) => {
  const chatContainerRef = useRef(null); // Ref for the sidebar container
  const inlineContainerRef = useRef(null); // Ref for the inline container
  const messagesEndRef = useRef(null);


  const [isLoadingMore, setIsLoadingMore] = useState(isLoadingMoreProp);
  const [hasMore, setHasMore] = useState(hasMoreProp);
  const [oldestMessageId, setOldestMessageId] = useState(null);
  const [isAtTop, setIsAtTop] = useState(false); // Track if scroll is at the very top
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);

  const currentContainerRef = isInline ? inlineContainerRef : chatContainerRef;

  // Function to scroll to the bottom
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    // Use timeout to ensure DOM is updated before scrolling
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior });
      // Reset isUserAtBottom after auto-scroll
      setIsUserAtBottom(true); 
    }, 0); // 0ms delay ensures it runs after the current render cycle
  }, []); // No dependencies needed as it reads refs directly

  // Debounced scroll handler to avoid excessive calculations
  const debouncedScroll = useCallback(
    debounce(() => {
      handleScroll();
    }, 100), // Debounce time: 100ms
    [isInline] // Recreate if isInline changes
  );

  const handleScroll = useCallback(() => {
    const container = isInline ? inlineContainerRef.current : chatContainerRef.current;
    if (!container) return;
    
    // Calculate how far we've scrolled
    const scrollTop = container.scrollTop;
    // Calculate the maximum possible scroll
    const scrollHeight = container.scrollHeight;
    const visibleHeight = container.clientHeight;
    
    // Consider user has scrolled up if not near bottom (within threshold)
    const isNearBottom = scrollHeight - scrollTop - visibleHeight < 100;
    setIsUserAtBottom(isNearBottom);
    
    // Check if we're at the very top
    if (scrollTop <= 5 && !isLoadingMore && hasMore && !isAtTop) {
      setIsAtTop(true);
    } else if (scrollTop > 5 && isAtTop) {
      setIsAtTop(false);
    }
  }, [isLoadingMore, hasMore, isAtTop, isInline]);

  // Set up scroll listener with debounce
  useEffect(() => {
    const container = isInline ? inlineContainerRef.current : chatContainerRef.current;
    if (!container) return;

    // Debounce the scroll handler to prevent too many calls
    let scrollTimeout;
    const debouncedScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        handleScroll();
      }, 150); // Wait for 150ms of scroll inactivity
    };

    container.addEventListener('scroll', debouncedScroll);
    return () => {
      container.removeEventListener('scroll', debouncedScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [handleScroll, isInline]);



  // Update oldestMessageId when messages change
  useEffect(() => {
    if (messages.length > 0) {
      setOldestMessageId(messages[0].id);
    }
  }, [messages]);

  // Effect to scroll to bottom when new messages arrive
  useEffect(() => {
    console.log("[ChatHistory Effect] Messages updated, length:", messages.length);
    // Only auto-scroll if the user was previously near the bottom
    // Check length > 0 to prevent scrolling on initial empty load
    if (messages.length > 0 && isUserAtBottom) {
      console.log("[ChatHistory Effect] Scrolling to bottom because user is at bottom.");
      // Use 'smooth' to animate the scroll
      scrollToBottom('smooth'); 
    }
  }, [messages, scrollToBottom, isUserAtBottom]); // Dependency: messages array, scrollToBottom callback, and isUserAtBottom

  // Handle new messages loaded event
  useEffect(() => {
    const handleNewMessages = (event) => {
      const { newMessages, hasMore: moreAvailable } = event.detail;
      
      // Log message loading state for debugging
      console.log('New messages loaded event received:', {
        count: newMessages?.length || 0,
        hasMore: moreAvailable
      });
      
      // If we've reached the end, explicitly log that
      if (!moreAvailable) {
        console.log('No more messages available - reached beginning of history');
        
        // Check if user is at the top of the scroll area
        if ((isInline ? inlineContainerRef.current : chatContainerRef.current) && (isInline ? inlineContainerRef.current : chatContainerRef.current).scrollTop === 0) {
          console.log('Currently at the top, updating isAtTop state');
          setIsAtTop(true);
        }
      }
      
      // Update component state
      setIsLoadingMore(false);
      setHasMore(moreAvailable);
    };

    window.addEventListener('newMessagesLoaded', handleNewMessages);
    return () => window.removeEventListener('newMessagesLoaded', handleNewMessages);
  }, [setIsLoadingMore, setHasMore]);

  // Function to generate unique message key
  const getMessageKey = (message, index) => {
    if (message.id) {
      // If the message has a unique ID from the backend, use it
      return message.id;
    }
    // Fallback to using timestamp and index for uniqueness
    return `${message.role}-${message.timestamp}-${index}`;
  };

  // Content rendering logic moved into a separate constant
  const chatContent = (
    <>
      {isLoadingInitialMessages ? (
        <div className={styles.initialLoading}>
          <div className={styles.loadingSpinner}></div>
          <div className={styles.loadingText}>Loading conversation history...</div>
        </div>
      ) : (
        <div className={styles.chatHistoryContent} ref={isInline ? inlineContainerRef : chatContainerRef}>
          {/* Status indicators at the top - only show when at the top */}
          {isLoadingMore ? (
            <div className={styles.loadingMore}>
              Loading more messages...
            </div>
          ) : isAtTop && !hasMore && messages.length > 0 ? (
            <div className={styles.noMoreMessages}>
              No more messages to load
            </div>
          ) : null}
          
          {/* Message content */}
          {messages.length === 0 ? (
            <div className={styles.emptyState}>
              <BsChatLeft className={styles.emptyIcon} />
              <p className={styles.emptyText}>
                No messages yet. Start a conversation to see your messages here.
              </p>
            </div>
          ) : (
            // Filter out automatic user queries but keep assistant responses
            messages
              .filter(message => {
                // Always keep assistant messages
                if (message.role === 'assistant') return true;
                // Filter out automatic user messages
                return !message.isAutomatic;
              })
              .map((message, index) => (
                <MessageBubble
                  key={getMessageKey(message, index)}
                  message={message}
                />
              ))
          )}
          <div ref={messagesEndRef} />
        </div>
      )}
    </>
  );

  // Log received messages length for debugging sidebar mode
  if (!isInline) {
    console.log('[ChatHistory Sidebar] Received messages prop length:', messages.length);
  }

  // Conditional rendering based on isInline
  if (isInline) {
    // Render directly in a container div for inline mode
    return (
      <div 
        ref={inlineContainerRef} 
        className={styles.chatHistoryInline}
        onScroll={isInline ? handleScroll : undefined} // Add scroll listener for inline mode if needed
      >
        {chatContent} 
      </div>
    );
  } else {
    // Log rendering of Sidebar variant
    console.log('[ChatHistory Sidebar] Rendering Sidebar component');
    // Render using Sidebar for the default sidebar mode
    return (
      <Sidebar 
        isOpen={isOpen} 
        onClose={onClose} 
        title="Messages"
        onNewConversation={() => {
          const event = new CustomEvent('startNewConversation');
          window.dispatchEvent(event);
        }}
      >
        {chatContent}
      </Sidebar>
    );
  }
};

export default ChatHistory;
